import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';

const GOLD = '#FFAC03';
const MUTED = 'var(--ace-chart-2, #6b7c93)';
const GREEN = '#2f9e6e';
const RED = '#d9534f';

// NII buildup: going-on vs going-off volume/rate decomposition, existing book
// path, and product (officer-type) mix — all derived from the live model.

const ForecastNiiBuildup = ({
  adjustedMonthly, newProductionMonthly, officerTypes, marketInputs, activeMacro, basePortfolio
}) => {
  const [pointMonth, setPointMonth] = useState(11);

  const rows = useMemo(() => {
    let cumulativeNew = 0;
    return adjustedMonthly.map((row, index) => {
      const production = newProductionMonthly[index] || { loans: 0, deposits: 0 };
      cumulativeNew += production.loans;
      const previous = index > 0 ? adjustedMonthly[index - 1].endingLoans : basePortfolio.loans;
      const goingOff = Math.max(0, previous * ((activeMacro.prepayment + 2.4) / 100) / 12);
      const netChange = row.endingLoans - previous;
      // Existing-team gross adds = net balance move + runoff − new-hire production.
      const existingGoingOn = Math.max(0, netChange + goingOff - production.loans);
      const existingBook = Math.max(0, row.endingLoans - cumulativeNew);
      const label = `FY${String(row.fiscalYearNumber).slice(2)} ${row.month}`;
      return {
        x: label,
        monthIndex: index,
        goingOnNew: production.loans,
        goingOnExisting: existingGoingOn,
        goingOnVol: production.loans + existingGoingOn,
        goingOffVol: -goingOff,
        netChange,
        existingBook,
        newBook: Math.min(cumulativeNew, row.endingLoans),
        portfolioYield: row.loanYield,
        goingOnRate: activeMacro.sofr + 2.35 + (index / 12) * 0.03,
        goingOffRate: Math.max(3.2, row.loanYield - 0.55),
        depositCost: row.depositCost,
        nii: row.nii
      };
    });
  }, [adjustedMonthly, newProductionMonthly, activeMacro, basePortfolio]);

  const mix = useMemo(() => {
    const totals = {};
    newProductionMonthly.forEach((cell) => {
      Object.entries(cell.byType || {}).forEach(([typeId, value]) => {
        const bucket = totals[typeId] = totals[typeId] || { loans: 0, deposits: 0 };
        bucket.loans += value.loans;
        bucket.deposits += value.deposits;
      });
    });
    const totalLoans = Object.values(totals).reduce((s, t) => s + t.loans, 0) || 1;
    return officerTypes
      .map((type) => {
        const bucket = totals[type.id] || { loans: 0, deposits: 0 };
        return {
          type: type.type,
          loans: bucket.loans,
          deposits: bucket.deposits,
          share: (bucket.loans / totalLoans) * 100,
          goingOnRate: activeMacro.sofr + type.loanSpreadBps / 100
        };
      })
      .filter((row) => row.loans > 0.01 || row.deposits > 0.01)
      .sort((a, b) => b.loans - a.loans);
  }, [newProductionMonthly, officerTypes, activeMacro]);

  const point = rows[pointMonth] || rows[0] || {};

  return (
    <div className="op3-suite">
      <div className="forecast-subpanel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">OP_04B1 · Going-on vs going-off</span>
          <h3>Monthly loan volume adds vs runoff, with the rates each carries</h3>
        </div>
        <div className="forecast-exec-pair">
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <ComposedChart data={rows}>
                <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
                <XAxis dataKey="x" minTickGap={44} />
                <YAxis tickFormatter={(v) => `$${Math.round(v)}`} width={56} />
                <Tooltip formatter={(v, n) => [`$${Number(v).toFixed(1)}MM`, n]} />
                <Legend />
                <Bar dataKey="goingOnExisting" name="Existing team going-on" fill={MUTED} stackId="on" />
                <Bar dataKey="goingOnNew" name="New-hire going-on" fill={GREEN} stackId="on" />
                <Bar dataKey="goingOffVol" name="Going-off volume" fill={RED} stackId="off" />
                <Line dataKey="netChange" name="Net balance change" stroke={GOLD} dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <ComposedChart data={rows}>
                <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
                <XAxis dataKey="x" minTickGap={44} />
                <YAxis tickFormatter={(v) => `${v.toFixed(1)}%`} width={52} domain={['auto', 'auto']} />
                <Tooltip formatter={(v, n) => [`${Number(v).toFixed(2)}%`, n]} />
                <Legend />
                <Line dataKey="goingOnRate" name="Going-on rate" stroke={GREEN} dot={false} strokeWidth={2} />
                <Line dataKey="goingOffRate" name="Going-off rate" stroke={RED} strokeDasharray="4 3" dot={false} strokeWidth={1.6} />
                <Line dataKey="portfolioYield" name="Portfolio yield" stroke={GOLD} dot={false} strokeWidth={2} />
                <Line dataKey="depositCost" name="Deposit cost" stroke={MUTED} strokeDasharray="2 3" dot={false} strokeWidth={1.4} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="forecast-subpanel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">OP_04B2 · Existing book through time</span>
          <h3>Existing book runs down as new production layers on</h3>
        </div>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <ComposedChart data={rows}>
              <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
              <XAxis dataKey="x" minTickGap={44} />
              <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(1)}B`} width={56} />
              <Tooltip formatter={(v, n) => [`$${Number(v).toFixed(0)}MM`, n]} />
              <Legend />
              <Area dataKey="existingBook" name="Existing book" stackId="b" fill={MUTED} stroke={MUTED} fillOpacity={0.45} />
              <Area dataKey="newBook" name="New production book" stackId="b" fill={GREEN} stroke={GREEN} fillOpacity={0.5} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="op4b-point">
          <label>
            Point-in-time
            <select value={pointMonth} onChange={(event) => setPointMonth(Number(event.target.value))}>
              {rows.map((row) => <option key={row.monthIndex} value={row.monthIndex}>{row.x}</option>)}
            </select>
          </label>
          <div className="forecast-submit-chips">
            <span className="forecast-health-chip neutral">Going-on ${(point.goingOnVol || 0).toFixed(1)}MM @ {(point.goingOnRate || 0).toFixed(2)}%</span>
            <span className="forecast-health-chip neutral">Going-off ${Math.abs(point.goingOffVol || 0).toFixed(1)}MM @ {(point.goingOffRate || 0).toFixed(2)}%</span>
            <span className="forecast-health-chip gold">Portfolio yield {(point.portfolioYield || 0).toFixed(2)}%</span>
            <span className="forecast-health-chip neutral">Spread capture {(((point.goingOnRate || 0) - (point.depositCost || 0))).toFixed(2)}%</span>
            <span className="forecast-health-chip green">NII ${(point.nii || 0).toFixed(1)}MM/mo</span>
          </div>
        </div>
      </div>

      <div className="forecast-subpanel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">OP_04B3 · Going-on mix by product</span>
          <h3>Product mix assumption per banker type — view-only (central assumption set)</h3>
        </div>
        <div className="forecast-home-table-wrap">
          <table className="forecast-home-table">
            <thead>
              <tr>
                <th>Banker type</th>
                <th>Term loans</th>
                <th>Line commitments</th>
                <th>Line utilization</th>
                <th>Going-on rate · term</th>
                <th>Going-on rate · lines</th>
                <th>60-mo loan prod</th>
                <th>60-mo deposit prod</th>
              </tr>
            </thead>
            <tbody>
              {officerTypes.map((type) => {
                const produced = mix.find((row) => row.type === type.type);
                return (
                  <tr key={type.id} className={produced ? '' : 'mix-inactive'}>
                    <td><strong>{type.type}</strong></td>
                    <td>{(100 - type.lineMix).toFixed(0)}%</td>
                    <td>{type.lineMix.toFixed(0)}%</td>
                    <td>{type.lineUtilization.toFixed(0)}%</td>
                    <td>{(activeMacro.sofr + type.loanSpreadBps / 100).toFixed(2)}%</td>
                    <td>{(activeMacro.sofr + type.lineSpreadBps / 100).toFixed(2)}%</td>
                    <td>{produced ? `$${produced.loans.toFixed(1)}MM` : '—'}</td>
                    <td>{produced ? `$${produced.deposits.toFixed(1)}MM` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="forecast-submit-note">
          Term/line split, utilization, and spreads are central product-mix assumptions by banker
          type; new-hire inputs cannot edit them. Production columns reflect the current hiring plan.
        </p>
      </div>
    </div>
  );
};

export default ForecastNiiBuildup;
